import React from 'react';
import { Shield, Eye, Bot } from 'lucide-react';
import { Button, Input, Textarea, Select } from '../../../components/ui';
import { Label } from '../../../components/ui';
import { FieldError, useFieldInvalid } from '../../../components/entity/FieldError.js';
import type { WizardState } from './hire-types.js';

interface HireStepIdentityProps {
  state: WizardState;
  onChange: (updates: Partial<WizardState>) => void;
}

const DEPARTMENTS = ['research', 'security', 'quality', 'testing', 'docs', 'performance', 'infrastructure', 'custom'];

const TYPES = [
  {
    value: 'guardian',
    label: 'Guardian',
    description: 'Protects systems, enforces rules, prevents violations',
    icon: Shield,
    colorClass: 'text-danger border-danger/30 bg-danger-muted',
  },
  {
    value: 'sentinel',
    label: 'Sentinel',
    description: 'Monitors patterns, detects anomalies, raises alerts',
    icon: Eye,
    colorClass: 'text-warning border-warning/30 bg-warning-muted',
  },
  {
    value: 'operative',
    label: 'Operative',
    description: 'Builds, improves, automates tasks and workflows',
    icon: Bot,
    colorClass: 'text-success border-success/30 bg-success-muted',
  },
] as const;

export const HireStepIdentity: React.FC<HireStepIdentityProps> = ({ state, onChange }) => {
  const nameInvalid = useFieldInvalid('name');
  const departmentInvalid = useFieldInvalid('department');
  const typeInvalid = useFieldInvalid('type');
  const mandateInvalid = useFieldInvalid('mandate');

  return (
    <div className="space-y-4">
      <div>
        <Label>Name</Label>
        <Input
          value={state.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="e.g. SecurityGuardian"
          invalid={nameInvalid}
        />
        <FieldError name="name" />
      </div>

      <div>
        <Label>Department</Label>
        <Select
          value={state.department}
          onChange={(e) => onChange({ department: e.target.value })}
          invalid={departmentInvalid}
        >
          <option value="">Select department...</option>
          {DEPARTMENTS.map((d) => (
            <option key={d} value={d}>
              {d.charAt(0).toUpperCase() + d.slice(1)}
            </option>
          ))}
        </Select>
        <FieldError name="department" />
      </div>

      <div>
        <Label>Type</Label>
        <div className={`grid grid-cols-3 gap-2 ${typeInvalid ? 'p-1 rounded-lg border border-danger' : ''}`}>
          {TYPES.map((t) => {
            const Icon = t.icon;
            const selected = state.type === t.value;
            return (
              <Button
                key={t.value}
                variant="ghost"
                aria-pressed={selected}
                onClick={() => onChange({ type: t.value })}
                className={`h-auto flex-col gap-1.5 whitespace-normal p-3 text-center ${
                  selected ? `${t.colorClass} border-2` : 'bg-surface-alt border hover:border-strong'
                }`}
              >
                <Icon size={20} className={selected ? '' : 'text-muted'} />
                <span className={`text-sm font-medium ${selected ? '' : 'text-body'}`}>{t.label}</span>
                <span className={`text-xs leading-tight ${selected ? 'opacity-80' : 'text-faint'}`}>
                  {t.description}
                </span>
              </Button>
            );
          })}
        </div>
        <FieldError name="type" />
      </div>

      <div>
        <Label hint="optional">Mandate</Label>
        <Textarea
          value={state.mandate}
          onChange={(e) => onChange({ mandate: e.target.value })}
          placeholder="What is this daemon's mission?"
          rows={3}
          invalid={mandateInvalid}
        />
        <FieldError name="mandate" />
      </div>
    </div>
  );
};
